import { Component, For } from "solid-js";
import { A } from "@solidjs/router";
import { useAuth } from "../App";
import PricingCards from "../components/PricingCards";

const rows = [
  { no: "4", horse: "Silver Meridian", signal: "Above", tone: "strong", fair: "4.20", market: "5.60", edge: "+33.3%" },
  { no: "7", horse: "Northern Index", signal: "Near", tone: "watch", fair: "6.10", market: "6.80", edge: "+11.5%" },
  { no: "2", horse: "Royal Circuit", signal: "Below", tone: "pass", fair: "3.75", market: "3.40", edge: "−9.3%" },
];

const Landing: Component = () => {
  const auth = useAuth();
  return (
    <>
      <section class="hero section-wrap">
        <div class="hero-copy">
          <div class="eyebrow"><span class="live-dot" /> Quantitative race intelligence</div>
          <h1>Find the price.<br /><em>Not the favourite.</em></h1>
          <p class="hero-lede">A mathematical horse-racing model that compares form, pace and live market data to produce transparent probability estimates and statistical edges.</p>
          <div class="hero-actions">
            <A class="button button-primary button-large" href={auth().user_id ? "/app" : "/login?mode=signup"}>Start analysing free <span>↗</span></A>
            <a class="text-link" href="#method">See how it works <span>↓</span></a>
          </div>
          <div class="trust-line"><span>✓ No card required</span><span>✓ Live HK & UK data</span><span>✓ Cancel anytime</span></div>
        </div>

        <div class="terminal-card" aria-label="Example Rating Races Pro analysis">
          <div class="terminal-top"><div><span class="status-dot" /> LIVE MODEL</div><span>SHA TIN · RACE 6 · 1400M</span></div>
          <div class="terminal-summary">
            <div><span>Market efficiency</span><strong>87.4%</strong></div>
            <div><span>Statistical differences</span><strong class="green">02</strong></div>
            <div><span>Model confidence</span><strong>HIGH</strong></div>
          </div>
          <div class="analysis-table">
            <div class="analysis-row analysis-head"><span>#</span><span>Runner</span><span>Model view</span><span>Fair</span><span>Market</span><span>Edge</span></div>
            <For each={rows}>{(row) => (
              <div class="analysis-row">
                <span>{row.no}</span><strong>{row.horse}</strong><span class={`signal signal-${row.tone}`}>{row.signal}</span><span>{row.fair}</span><span>{row.market}</span><b class={row.edge.startsWith("+") ? "green" : "red"}>{row.edge}</b>
              </div>
            )}</For>
          </div>
          <div class="terminal-foot"><span>Last recalibrated 08:42:16</span><span class="pulse-label">● Markets updating</span></div>
        </div>
      </section>

      <section class="proof-strip">
        <div><strong>Form</strong><span>Fundamental ratings</span></div>
        <div><strong>Price</strong><span>Live market context</span></div>
        <div><strong>Edge</strong><span>Statistical comparison</span></div>
        <div><strong>Clarity</strong><span>Transparent model outputs</span></div>
      </section>

      <section class="section section-wrap" id="method">
        <div class="section-kicker">THE METHOD</div>
        <div class="section-heading"><h2>Clarity from complexity.</h2><p>We combine fundamental handicapping with live market intelligence—so every signal is grounded in evidence, not instinct.</p></div>
        <div class="method-grid">
          <article><span>01</span><div class="feature-icon">⌁</div><h3>Measure true ability</h3><p>Speed figures, sectionals, weight, draw, going and class are normalised into a comparable performance rating.</p></article>
          <article><span>02</span><div class="feature-icon">◫</div><h3>Read the market</h3><p>Live tote and exchange prices reveal the public probability—and where conviction is moving before the off.</p></article>
          <article><span>03</span><div class="feature-icon">↗</div><h3>Quantify the difference</h3><p>The model compares its estimated probability with the market and reports the mathematical difference for independent analysis.</p></article>
        </div>
      </section>

      <section class="feature-band" id="features">
        <div class="section-wrap feature-inner">
          <div><div class="section-kicker">BUILT FOR ANALYSIS</div><h2>Statistical analysis, not betting advice.</h2><p class="feature-lede">Explore modelled probabilities and market comparisons as information, then interpret the data and make your own independent decisions.</p></div>
          <div class="feature-list">
            <div><b>01</b><span><strong>Probability differences</strong><small>Model estimates compared with current market probabilities.</small></span></div>
            <div><b>02</b><span><strong>Live price comparison</strong><small>See fair odds beside current market prices.</small></span></div>
            <div><b>03</b><span><strong>Deep race cards</strong><small>Every runner, factor and signal in one clean view.</small></span></div>
            <div><b>04</b><span><strong>Model transparency</strong><small>Clear statistical outputs designed for your own interpretation.</small></span></div>
          </div>
        </div>
      </section>

      <section class="section section-wrap pricing-section" id="pricing">
        <div class="section-kicker">SIMPLE PRICING</div>
        <div class="pricing-heading"><h2>One product. Two ways to join.</h2><p>Create your account free, then unlock the complete Rating Races Pro platform.</p></div>
        <PricingCards />
      </section>

      <footer class="footer section-wrap">
        <div class="brand"><span class="brand-mark">R</span><span>Rating Races <strong>Pro</strong></span></div>
        <p>Understand the probabilities. Make your own decisions.</p>
        <div><A href="/login">Log in</A><a href="#pricing">Pricing</a><span>© 2026 Rating Races Pro</span></div>
      </footer>
    </>
  );
};

export default Landing;
